import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import Footer from './Footer';
import { 
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const LayoutWrapper = ({ children }) => {
  const { user, logout } = useOutletContext();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Funzione per ottenere le iniziali dell'utente per l'avatar
  const getUserInitials = () => {
    if (user && user.nome && user.cognome) {
      return `${user.nome.charAt(0)}${user.cognome.charAt(0)}`.toUpperCase();
    }
    return user?.username?.charAt(0).toUpperCase() || 'U';
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 via-white to-slate-50">
      {/* Header - Stile Apple */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100/50 sticky top-0 z-50 shadow-lg shadow-gray-200/20">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-18">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link to="/" className="flex items-center group">
                  <div className="relative">
                    <img 
                      src="/logo.png" 
                      alt="Logo Wapoo" 
                      className="h-10 sm:h-12 w-auto transition-all duration-300 group-hover:scale-105" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-lg"></div>
                  </div>
                </Link>
              </div>
            </div>
            
            {/* Menu utente */}
            <div className="flex items-center">
              <div className="ml-3 relative">
                <button
                  className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500/50 p-1.5 transition-all duration-200 hover:bg-white/50"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  <span className="sr-only">Menu utente</span>
                  <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/30">
                    {getUserInitials()}
                  </div>
                </button>
                
                {/* Menu a tendina utente - Stile Apple */}
                {isUserMenuOpen && (
                  <>
                    {/* Overlay per chiudere il menu cliccando fuori */}
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setIsUserMenuOpen(false)}
                    />
                    
                    <div className="origin-top-right absolute right-0 mt-3 w-72 rounded-2xl shadow-2xl bg-white/90 backdrop-blur-xl ring-1 ring-gray-200/50 z-20 border border-white/20">
                      <div className="py-3">
                        {/* Header del menu con info utente - Stile Apple */}
                        <div className="px-6 py-4 border-b border-gray-100/80">
                          <div className="flex items-center">
                            <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold shadow-lg">
                              {getUserInitials()}
                            </div>
                            <div className="ml-4">
                              <div className="font-semibold text-gray-900 text-base tracking-tight">
                                {user?.nome} {user?.cognome}
                              </div>
                              <div className="text-sm text-gray-500 font-medium">
                                {user?.email}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Voci del menu - Stile Apple */}
                        <div className="px-2 py-2">
                          <Link
                            to="/profilo"
                            className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <UserCircleIcon className="mr-3 h-5 w-5 text-gray-500 group-hover:text-blue-500 transition-colors duration-200" />
                            <span className="font-medium">Profilo</span>
                          </Link>
                          
                          <a
                            href="https://my.wapoo.it"
                            className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-5 w-5 text-gray-500 group-hover:text-emerald-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            <span className="font-medium">Torna alla home</span>
                          </a>
                          
                          <Link
                            to="/nlt/"
                            className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50/80 rounded-xl transition-all duration-200 group"
                            role="menuitem"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-5 w-5 text-gray-500 group-hover:text-purple-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0z M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0M15 17a2 2 0 104 0" />
                            </svg>
                            <span className="font-medium">Dashboard NLT</span>
                          </Link>
                        </div>
                        
                        <div className="border-t border-gray-100/80 mx-2 mt-2">
                          <div className="px-2 py-2">
                            <button
                              className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50/80 rounded-xl transition-all duration-200 group"
                              role="menuitem"
                              onClick={() => {
                                setIsUserMenuOpen(false);
                                handleLogout();
                              }}
                            >
                              <ArrowLeftOnRectangleIcon className="mr-3 h-5 w-5 text-red-500 group-hover:text-red-600 transition-colors duration-200" />
                              <span className="font-medium">Logout</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Contenuto principale */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <Footer variant="minimal" />
    </div>
  );
};

export default LayoutWrapper; 